/* ============================================================
 * 招聘模块 — C 端 API
 *
 * 接口对齐后端设计文档 §十七 + 前端对接指南 §十九（C 端 15 个接口）。
 *
 * 通用约定（与工单模块一致）：
 *   - 列表：AjaxResult（{code, msg, data: {rows, total}}）→ ApiResponse<{rows, total}>
 *   - 动作/详情：{code, msg, data} → ApiResponse<T>
 *   - 错误统一抛 ApiError，前端用 getErrorMessage 取 msg
 *
 * 【错误处理约定 2026-10-19】后端 commit 2e41808 已删掉自造的 4003/4004/404/403：
 *   - 未登录 → `401`（走 client.ts 现有刷新/跳登录逻辑，本文件不用处理）
 *   - 其余业务异常 → `500` + msg，前端**直接 toast 这句话**
 *   → 本文件**不按文案分支、不做错误跳转**
 *   → 企业认证 / 无简历的引导，走**进页面前主动调接口预判**，不等报错
 *
 * 【联调环境】测试环境是 `testapi.aixuntu.com`（见 .env.staging），
 *   不是 `api.aixuntu.com`（开发环境，**未部署招聘模块，打过去会 401**）。
 * ============================================================ */

import http from '@/api/client'
import type { ApiResponse } from '@/types/api'
import type {
  ApplicantDetailVO,
  ApplicantItemVO,
  ApplicantMarkPayload,
  ApplicantQuery,
  JobApplyPayload,
  JobDetailVO,
  JobDictVO,
  JobIdPayload,
  JobListItemVO,
  JobSavePayload,
  JobSquareQuery,
  MyApplicationDetailVO,
  MyApplicationItemVO,
  ResumeSavePayload,
  ResumeVO,
} from '@/types/job'

/** 列表类接口的统一响应形状。 */
type PageData<T> = { rows: T[]; total: number }

/* ============================================================
 * 公共（匿名可调）
 * ============================================================ */

/**
 * GET /app/job/dict — 匿名可调。
 *
 * 四个下拉都是稳定的可枚举值（实测：岗位类型 4 项 / 学历 5 项 / 经验 6 项 / 城市 11 项），
 * 前端做缓存，避免每次进页面重拉。缓存逻辑见 `stores/job.ts`。
 *
 * ⚠️ 经验下拉里 FRESH 的 label 实际是「应届生」，**文案一律用这里返回的 label，
 *    不要在前端硬编码**。
 */
export function getJobDict(): Promise<ApiResponse<JobDictVO>> {
  return http.get<ApiResponse<JobDictVO>>('/app/job/dict').then((r) => r.data)
}

/** GET /app/job/square — 招聘广场，匿名可调。
 *
 *  后端只返回 `OPENING` 且未过截止时间的岗位，前端**不需要再过滤一层**。
 *  query 全部可选；不传 sort = 最新发布。
 */
export function getJobSquare(
  query: JobSquareQuery = {},
): Promise<ApiResponse<PageData<JobListItemVO>>> {
  return http
    .get<ApiResponse<PageData<JobListItemVO>>>('/app/job/square', { params: query })
    .then((r) => r.data)
}

/** GET /app/job/detail/{id} — 岗位详情，匿名可看（登录后额外返回是否已投递）。
 *
 *  已结束 / 已下线岗位仍可通过 id 直达（用于「我的申请」跳转），此时投递按钮置灰。
 */
export function getJobDetail(id: number): Promise<ApiResponse<JobDetailVO>> {
  return http.get<ApiResponse<JobDetailVO>>(`/app/job/detail/${id}`).then((r) => r.data)
}

/* ============================================================
 * 求职者侧（需登录）
 * ============================================================ */

/**
 * GET /app/job/resume — 我的简历。
 *
 * 没有简历时返回**空壳**：`id: null`、`version: 0`、三个 list 为 `[]`，
 * 但 `name` / `phone` 已按登录信息预填。
 * → 页面判断「有无简历」只看 `id === null`。
 */
export function getMyResume(): Promise<ApiResponse<ResumeVO>> {
  return http.get<ApiResponse<ResumeVO>>('/app/job/resume').then((r) => r.data)
}

/**
 * POST /app/job/resume/save — 保存简历（覆盖式，后端 version +1），返回简历 ID。
 *
 * ⚠️ 子表整体提交：三个 list 必须带全量，空也要传 `[]`。
 * ⚠️ 附件简历先走 `utils/upload.ts` 直传拿 objectKey 再提交（**不传完整 URL**）。
 */
export function saveMyResume(payload: ResumeSavePayload): Promise<ApiResponse<number>> {
  return http.post<ApiResponse<number>>('/app/job/resume/save', payload).then((r) => r.data)
}

/**
 * POST /app/job/apply — 投递岗位，返回投递记录 ID。
 *
 * ⚠️ 重复投递**不报错**，同样返回 code 200 + msg「投递成功」+ 已有记录的 id。
 *   所以前端要按「成功」处理（跳「我的申请」），不要弹错误提示。
 * ⚠️ 无简历时后端返 `500` + msg「请先完善简历后再投递」——但正常路径是
 *   **进投递页前先调 getMyResume 判 id 是否为 null**，别等这个报错。
 */
export function applyJob(payload: JobApplyPayload): Promise<ApiResponse<number>> {
  return http.post<ApiResponse<number>>('/app/job/apply', payload).then((r) => r.data)
}

/** GET /app/job/my/applications — 我的申请列表。
 *
 *  状态**恒为「已投递」**（企业标记不通知用户、不对外暴露）。
 */
export function getMyApplications(
  params: { pageNum?: number; pageSize?: number } = {},
): Promise<ApiResponse<PageData<MyApplicationItemVO>>> {
  return http
    .get<ApiResponse<PageData<MyApplicationItemVO>>>('/app/job/my/applications', { params })
    .then((r) => r.data)
}

/** GET /app/job/my/applications/{applyId} — 我的申请详情（含投递时的简历快照）。 */
export function getMyApplicationDetail(applyId: number): Promise<ApiResponse<MyApplicationDetailVO>> {
  return http
    .get<ApiResponse<MyApplicationDetailVO>>(`/app/job/my/applications/${applyId}`)
    .then((r) => r.data)
}

/* ============================================================
 * 企业侧（需登录 + 企业认证已通过）
 *
 * 认证门槛由后端兜底（未认证返 500 +「请先完成企业认证」）。
 * 前端在**进这些页面前**调 `getEnterpriseStatus()`（api/apply.ts 已有）预判并展示引导页，
 * 不靠错误码触发跳转。
 * ============================================================ */

/** GET /app/job/manage/list — 企业招聘工作台。
 *
 *  tab：opening（默认）/ draft / closed / takedown。
 *  列表项结构与招聘广场**完全相同**（后端复用同一个 VO）。
 */
export function getManageJobList(
  params: { tab?: string; pageNum?: number; pageSize?: number } = {},
): Promise<ApiResponse<PageData<JobListItemVO>>> {
  return http
    .get<ApiResponse<PageData<JobListItemVO>>>('/app/job/manage/list', { params })
    .then((r) => r.data)
}

/**
 * POST /app/job/manage/save?action=publish|draft — 发布 / 编辑岗位，返回岗位 ID。
 *
 * ⚠️ `action` 在 **query 上**，不在 body：
 *   - `publish`（默认）发布，校验必填
 *   - `draft` 存草稿，不校验必填
 * `payload.id` 为空 = 新建，非空 = 编辑。
 *
 * 行为提醒（后端 §17.4 第 4 条）：
 *   - 编辑 `OPENING` 岗位 → 保存后**立即生效**
 *   - 编辑 `CLOSED` 岗位 → 保存后**不自动重开**，需另调 reopenManageJob
 *   - `TAKEN_DOWN` 岗位企业不可编辑
 */
export function saveManageJob(
  payload: JobSavePayload,
  action: 'publish' | 'draft' = 'publish',
): Promise<ApiResponse<number>> {
  return http
    .post<ApiResponse<number>>('/app/job/manage/save', payload, { params: { action } })
    .then((r) => r.data)
}

/** POST /app/job/manage/close — 关闭岗位（OPENING → CLOSED）。 */
export function closeManageJob(payload: JobIdPayload): Promise<ApiResponse<null>> {
  return http.post<ApiResponse<null>>('/app/job/manage/close', payload).then((r) => r.data)
}

/** POST /app/job/manage/reopen — 重新开启岗位（CLOSED → OPENING）。 */
export function reopenManageJob(payload: JobIdPayload): Promise<ApiResponse<null>> {
  return http.post<ApiResponse<null>>('/app/job/manage/reopen', payload).then((r) => r.data)
}

/** POST /app/job/manage/delete — 删除岗位。**仅草稿可删**（软删）。 */
export function deleteManageJob(payload: JobIdPayload): Promise<ApiResponse<null>> {
  return http.post<ApiResponse<null>>('/app/job/manage/delete', payload).then((r) => r.data)
}

/** GET /app/job/manage/applicants — 候选人列表。
 *
 *  filterStatus 不传 = 全部；传则按标记筛选
 *  （APPLIED 未处理 / VIEWED 已查看 / CONTACTED 已联系 / REJECTED 不合适）。
 */
export function getApplicants(
  params: ApplicantQuery,
): Promise<ApiResponse<PageData<ApplicantItemVO>>> {
  return http
    .get<ApiResponse<PageData<ApplicantItemVO>>>('/app/job/manage/applicants', { params })
    .then((r) => r.data)
}

/** GET /app/job/manage/applicants/{applyId} — 候选人简历详情（抽屉）。
 *
 *  ⚠️ 响应里的 `hint`（"以下是用户投递时的简历内容"）是契约要求，
 *     抽屉标题必须展示，避免企业误认为最新版。
 */
export function getApplicantDetail(applyId: number): Promise<ApiResponse<ApplicantDetailVO>> {
  return http
    .get<ApiResponse<ApplicantDetailVO>>(`/app/job/manage/applicants/${applyId}`)
    .then((r) => r.data)
}

/**
 * POST /app/job/manage/applicants/mark — 候选人标记。
 *
 * ⚠️ **只把要改的字段放进 payload**：
 *   - 只改状态 → `{applyId, status}`，不带 matchTag（= 标签不动）
 *   - 打标签 → `{applyId, matchTag: 'MATCH'}`
 *   - **清空标签 → 显式传 `matchTag: ''`**（空字符串）
 * `status` 可随时改回 `APPLIED`。
 */
export function markApplicant(payload: ApplicantMarkPayload): Promise<ApiResponse<null>> {
  return http
    .post<ApiResponse<null>>('/app/job/manage/applicants/mark', payload)
    .then((r) => r.data)
}
